import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { decompressFromEncodedURIComponent } from 'lz-string';
import * as moment from 'moment/moment';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import { UnitNonPmsService } from '@app/services/unit-non-pms.service';
import { setSelectedPropertyIds } from '@dashboards/store/actions/property-selector.actions';
import {
  selectSelectedPortfolioId,
  selectSelectedPropertyIds,
} from '@dashboards/store/selectors/property-selector.selectors';
import { loadAllUsers } from '@main-application/administration/store/actions/administration.actions';
import { NonPmsAddModalComponent } from '@main-application/boards/non-pms-add-modal/non-pms-add-modal.component';
import { InspectionsHeaderViewEnum } from '@main-application/inspections/models/inspections-header.model';
import { InspectionSummaryAppliedFilters } from '@main-application/inspections/models/legacy-inspection-summary.model';
import { InspectionService } from '@main-application/inspections/services/inspection.service';
import { InspectionsBulkDownloadService } from '@main-application/inspections/services/inspections-bulk-download.service';
import {
  selectBulkInspectionFilter,
  selectInReviewInspectionFilter,
  selectInReviewInspections,
  selectInspectionFilter,
  selectInspections,
  selectRepeatingInspectionFilter,
  selectRepeatingInspections,
  selectRepeatingInspectionsLoading,
} from '@main-application/inspections/store/inspections.selectors';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import { AllTimeStartingPeriod } from '@main-application/service/service.constants';
import {
  SummaryReportSettingsModel,
  SummaryType,
} from '@main-application/shared/services/summary-report-settings.service';
import { TemplateEditorDialogService } from '@main-application/shared/template-editor-dialog/services/template-editor-dialog.service';
import { selectCombinedSummarySetting } from '@main-application/store/selectors/summary-report-settings.selectors';
import { GetRecurrencePeriodTypeList } from '@shared/constants/recurrence-period-type-list.const';
import { EmptyValues } from '@shared/enums/common.enum';
import { LocalStorageDataEnum } from '@shared/enums/local-storage-data.enum';
import { RouteData } from '@shared/enums/route-data';
import { RoutePath } from '@shared/enums/route-path.enum';
import { UserType } from '@shared/enums/user-type';
import {
  FilterDescriptionConfig,
  buildFilterDescriptionEntry,
} from '@shared/functions/build-report-description.function';
import { enumToSource } from '@shared/functions/enum-to-source.function';
import {
  convertFiltersToParams,
  extractFiltersFromParams,
  normalizeQueryParams,
} from '@shared/functions/filter-to-query-params.function';
import { NotSavedGuard } from '@shared/guards/not-saved-guard.guard';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { IForm } from '@shared/types/form-model.type';
import { getStorageItem, setStorageItem } from '@shared/utils/extensions';
import { filterNullish$ } from '@shared/utils/rxjs/filter-nullish.rxjs.util';
import { skipEqual$ } from '@shared/utils/rxjs/skip-equal.rxjs.util';
import {
  BulkInspectionFilterForm,
  IInspectionFilter,
  IInspectionFilterForm,
  RepeatingInspectionFilterForm,
} from '@template/interfaces/filter.interface';
import { loadAllUserTemplates } from '@template/store/template.actions';
import { selectTemplates } from '@template/store/template.selectors';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { DefaultModalConfigConstant } from '@ui-components/modals/config/default-modal-config.constant';
import { ModalsService } from '@ui-components/modals/modals.service';

import { SummaryFiltersFromListConfig } from './components/legacy-inspections-summary/inspections-summary.model';
import {
  ASSIGNEE_FILTER_DIVIDER_POSITION,
  InspectionHasIssues,
  InspectionQueryType,
  InspectionsListConstants,
} from './constants';
import { SelectPortfolioDialogComponent } from './dialogs/select-portfolio';
import { InspectionSectionsExpandState } from './interfaces';
import {
  InspectionByStatusFilter,
  InspectionByStatusFilterLabels,
  InspectionRecurrencePeriod,
  InspectionScheduleType,
  InspectionStatus,
} from '../../models/rest-inspections-model.interface';
import {
  setBulkInspectionFilter,
  setInReviewInspectionsFilter,
  setInspectionsFilter,
  setNewlyAddedInspection,
  setRepeatingInspectionsFilter,
} from '../../store/inspections.actions';
import {
  InspectionAddDialogComponent,
  InspectionDialogData,
} from '../inspection-add-dialog/inspection-add-dialog.component';
import { InspectionsHeaderComponent } from '../inspections-header/inspections-header.component';

export enum InspectionsType {
  User = 'user',
  Dynamic = 'dynamic',
  Repeating = 'repeating',
  Resident = 'resident',
  InReview = 'in-review',
  Bulk = 'bulk',
}

const InspectionTabStorageKeys = {
  [InspectionsType.User]: LocalStorageDataEnum.INSPECTION_LIST_USER_FILTER,
  [InspectionsType.Dynamic]: LocalStorageDataEnum.INSPECTION_LIST_DYNAMIC_FILTER,
  [InspectionsType.Repeating]: LocalStorageDataEnum.INSPECTION_LIST_REPEATING_FILTER,
  [InspectionsType.Resident]: LocalStorageDataEnum.INSPECTION_LIST_RESIDENT_FILTER,
  [InspectionsType.InReview]: LocalStorageDataEnum.INSPECTION_LIST_IN_REVIEW_FILTER,
  [InspectionsType.Bulk]: LocalStorageDataEnum.INSPECTION_LIST_BULK_FILTER,
};

@UntilDestroy()
@Component({
  selector: 'app-inspections-list',
  templateUrl: './inspections-list.component.html',
  styleUrls: ['./inspections-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectionsListComponent extends ComponentAbstract implements OnInit, OnDestroy, NotSavedGuard {
  private readonly _currentDate = this.timezoneService.getCurrentDateOnly();

  formGroup!: FormGroup<IForm<Partial<IInspectionFilterForm>>>;

  readonly statuses = enumToSource(InspectionStatus);
  readonly hasIssues = InspectionsListConstants.hasIssues;
  readonly timeframes = InspectionsListConstants.timeFrames;
  readonly periodDateSearchFields = InspectionsListConstants.periodDateSearchFields;
  readonly filterGroupKey = LocalStorageDataEnum.InspectionFilterOffsetKey;
  readonly dividerPosition = ASSIGNEE_FILTER_DIVIDER_POSITION;
  readonly InspectionsType = InspectionsType;
  readonly TimeFrame = InspectionsListConstants.TimeFrame;
  readonly InspectionsHeaderViewEnum = InspectionsHeaderViewEnum;

  protected filter: IInspectionFilter = {};

  templates$: Observable<IRadioButtonOption<number>[]> = this.initTemplates();
  inspectionTemplateCategories$: Observable<IRadioButtonOption<number>[]> = this.templateEditorDialogService
    .getInspectionTemplateCategories()
    .pipe(
      map(data => [{ value: -1, label: 'N/A' }, ...data.map(el => ({ value: el.id, label: el.name }))]),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      })
    );
  parentInspectionIds$: Observable<IRadioButtonOption<number>[]> = this.initTemplatesWithParentInspectionId();
  dynamicInspectionIds$: Observable<IRadioButtonOption<number>[]> = this.initTemplatesWithDynamicInspectionId();
  reccurenceOptions$: Observable<IRadioButtonOption<InspectionRecurrencePeriod | string>[]> =
    this.initRecurenceOptions();
  repeatingInspectionsIds$ = this.getRepeatingInspectionsIds();
  dynamicInspectionsIds$ = this.getDynamicInspectionsIds();
  assignees$ = this.inspectionService.getSortedUsers();

  dynamicItemsAmount$ = new BehaviorSubject(0);
  repeatingItemsAmount$ = new BehaviorSubject(0);
  itemsAmount$ = new BehaviorSubject(0);
  isUnitAdding$ = new BehaviorSubject(false);
  portfolioId$ = new BehaviorSubject<number | null>(null);
  summaryFiltersFromListConfig$ = new BehaviorSubject<SummaryFiltersFromListConfig | null>(null);
  currentSummarySettings: SummaryReportSettingsModel;
  isShowCustomPeriod$ = new BehaviorSubject(false);
  customPeriod$ = new BehaviorSubject<{ startDate: string; endDate: string }>(null);
  inspectionsViewType$ = new BehaviorSubject<InspectionsHeaderViewEnum>(InspectionsHeaderViewEnum.LIST);

  maxHeight = 400;
  inspectionsType: InspectionsType;
  sectionsExpandState: InspectionSectionsExpandState = InspectionsListConstants.sectionsExpandInitialState;

  isResident$ = new BehaviorSubject(false);
  editInspectionOpened = false;

  summaryModalPending$ = new BehaviorSubject(false);
  inReviewFormExcludedItems = [
    'doNotReturnContent',
    'inReviewOnly',
    'isResident',
    'returnStandardAndResidentBoth',
    'unitOrAssignee',
  ];

  excludedFromFilterCounter: string[];
  openFilter: { [key: string]: string };
  openDropdown: { [key: string]: string };
  doNotStoreParams = false;
  showInstances = false;
  filterParams: Partial<IInspectionFilterForm>;
  inspectionByStatusFilter: string;
  inspectionByStatusFilterDescription: { name: string; additional?: number; tooltip?: string }[] = [];
  isReportView: boolean;
  canBeDestroyed = false;
  archiveAll$ = new BehaviorSubject<number | null>(null);

  @ViewChild('inspectionHeader') inspectionHeader: InspectionsHeaderComponent;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.calculateMaxHeight();
  }

  constructor(
    cdr: ChangeDetectorRef,
    private router: Router,
    private readonly store: Store<{}>,
    private readonly formBuilder: FormBuilder,
    private readonly timezoneService: TimezoneService,
    private readonly snackbarService: SnackbarService,
    private readonly inspectionService: InspectionService,
    private readonly bulkDownloadService: InspectionsBulkDownloadService,
    private dialog: MatDialog,
    private actions$: Actions,
    private unitNonPmsService: UnitNonPmsService,
    private route: ActivatedRoute,
    private modalsService: ModalsService,
    private templateEditorDialogService: TemplateEditorDialogService
  ) {
    super(cdr);

    this.doNotStoreParams =
      this.route.snapshot.queryParams[RouteData.DO_NOT_STORE_PARAMS] &&
      this.route.snapshot.queryParams[RouteData.DO_NOT_STORE_PARAMS] === 'true';

    this.onApplyQueryParams();
  }

  get filterStorageKey(): string {
    return this.inspectionsType
      ? InspectionTabStorageKeys[this.inspectionsType]
      : this.LocalStorageDataEnum.INSPECTION_LIST_USER_FILTER;
  }

  get isUserResidentPage(): boolean {
    return InspectionsType.User === this.inspectionsType || InspectionsType.Resident === this.inspectionsType;
  }

  get startDateControl(): AbstractControl {
    return this.formGroup.controls.startDate as FormControl;
  }

  get endDateControl(): AbstractControl {
    return this.formGroup.controls.endDate as FormControl;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.snackbarService.dismiss();

    const queryParams = { ...this.route.snapshot.queryParams };
    const hasChanges = 'doNotStoreParams' in queryParams || 'inspectionByStatusFilter' in queryParams;

    if (hasChanges) {
      this.inspectionByStatusFilter = null;
      this.doNotStoreParams = null;
      delete queryParams['doNotStoreParams'];
      delete queryParams['inspectionByStatusFilter'];
    }
  }

  ngOnInit(): void {
    this.initSummarySettings();

    this.route.url
      .pipe(take(1))
      .subscribe(urlSegments => {
        const type = urlSegments[0]?.path;
        this.inspectionsType = Object.values(InspectionsType).includes(type as InspectionsType)
          ? (type as InspectionsType)
          : InspectionsType.User;

        const isReportRoute =
          urlSegments.length === 3 &&
          (urlSegments[0].path === RoutePath.USER || urlSegments[0].path === RoutePath.RESIDENT) &&
          urlSegments[1].path === RoutePath.REPORT &&
          !!urlSegments[2]?.path;

        this.isReportView = isReportRoute;

        if (this.isReportView) {
          const routeParams: Params = {
            ...this.route.snapshot.queryParams,
            inspectionByStatusFilter: this.route.snapshot.params['inspectionByStatusFilter'],
          };

          const hash = window.location.hash;
          if (hash.startsWith('#filters=')) {
            const compressed = hash.slice(9);
            const decompressedStr = decompressFromEncodedURIComponent(compressed);

            if (decompressedStr && typeof decompressedStr === 'string') {
              const decompressedObj = JSON.parse(decompressedStr);

              for (const [key, value] of Object.entries(decompressedObj)) {
                if (routeParams[key] == null && value != null) {
                  routeParams[key] = value;
                }
              }
            }
          }

          if (routeParams.startDate === AllTimeStartingPeriod) {
            this.inspectionByStatusFilterDescription = [
              {
                name: `all time`,
              },
            ];
          } else {
            this.inspectionByStatusFilterDescription = [
              {
                name: `${moment(routeParams.startDate).format(
                  this.isCurrentYear(routeParams.startDate) ? 'MM/DD' : 'MM/DD/YYYY'
                )} ${EmptyValues.TIRET} ${moment(routeParams.endDate).format(
                  this.isCurrentYear(routeParams.endDate) ? 'MM/DD' : 'MM/DD/YYYY'
                )}`,
              },
            ];
          }

          this.setFilterConfigs(routeParams);
        }

        this.initInspectionsList();
      })
      .untilDestroyed(this);
  }

  private setFilterConfigs(routeParams: Params): void {
    const configs: FilterDescriptionConfig[] = [
      {
        key: 'portfolioId',
        labelSingular: 'Portfolio',
        labelPlural: 'Portfolios',
        name: routeParams.portfolioName,
      },
      {
        key: 'collectionIds',
        labelSingular: 'Property Group',
        labelPlural: 'Property Groups',
        canIncludeNA: true,
        name: routeParams.collectionNames,
      },
      {
        key: 'propertyIds',
        labelSingular: 'Property',
        labelPlural: 'Properties',
        canIncludeNA: true,
        name: routeParams.propertyNames,
      },
      {
        key: 'unitTypes',
        labelSingular: 'Type',
        labelPlural: 'Types',
        canIncludeNA: true,
      },
      {
        key: 'templateIds',
        labelSingular: 'Template',
        labelPlural: 'Templates',
        canIncludeNA: true,
      },
      {
        key: 'ticketCategoryIds',
        labelSingular: 'Category',
        labelPlural: 'Categories',
        canIncludeNA: true,
      },
      {
        key: 'assigneeIds',
        labelSingular: 'Assignee',
        labelPlural: 'Assignees',
        canIncludeNA: true,
      },
      {
        key: 'hasIssuesIds',
        labelSingular: 'Has Issues',
        labelPlural: 'Has Issues',
        canIncludeNA: false,
      },
      {
        key: 'isArchivedIds',
        labelSingular: 'Is Archived',
        labelPlural: 'Is Archived',
        canIncludeNA: false,
      },
      {
        key: 'statusIds',
        labelSingular: 'Status',
        labelPlural: 'Statuses',
        canIncludeNA: true,
      },
      {
        key: 'dynamicInspectionIds',
        labelSingular: 'Dynamic Inspection',
        labelPlural: 'Dynamic Inspections',
        canIncludeNA: true,
      },
      {
        key: 'parentInspectionIds',
        labelSingular: 'Parent Inspection',
        labelPlural: 'Parent Inspections',
        canIncludeNA: true,
      },
    ];

    configs.forEach(config => {
      buildFilterDescriptionEntry(routeParams, config, entry => this.inspectionByStatusFilterDescription.push(entry));
    });
  }

  private initInspectionsList(): void {
    this.excludedFromFilterCounter = this.getExcludedFromFilterCounter();

    this.formGroup = this.initFormGroup();
    this.filter = this.formGroup.getRawValue();
    this.initQueryTypeChanges();

    this.sectionsExpandState = {
      Dynamic: this.inspectionsType === InspectionsType.Dynamic,
      Repeating: this.inspectionsType === InspectionsType.Repeating,
      Standard: this.inspectionsType === InspectionsType.User,
      Resident: this.inspectionsType === InspectionsType.Resident,
      InReview: this.inspectionsType === InspectionsType.InReview,
      Bulk: this.inspectionsType === InspectionsType.Bulk,
    };

    if (this.inspectionsType === 'user') {
      this.isResident$.next(false);
    } else if (this.inspectionsType === 'resident') {
      this.isResident$.next(true);
    }

    if (this.inspectionsType !== InspectionsType.User && this.inspectionsType !== InspectionsType.InReview) {
      this.formGroup.patchValue({
        parentInspectionId: null,
        dynamicInspectionId: null,
        timeframe: null,
      });
    }

    if (this.inspectionsType !== InspectionsType.User && this.inspectionsType !== InspectionsType.InReview) {
      this.formGroup.patchValue({
        status: null,
      });
    }

    this.onApplyFilters();

    this.store.dispatch(loadAllUserTemplates());
    this.useLoadUsers();

    this.initFormGroupListener();

    this.actions$
      .pipe(
        ofType(setNewlyAddedInspection),
        tap(action => {
          const sectionsExpand = Object.keys(this.sectionsExpandState)
            .filter(key => this.sectionsExpandState[key])?.[0]
            ?.toLowerCase();

          if (sectionsExpand && sectionsExpand !== this.inspectionsType) this.clearFilter(true);

          if (action.inspection) {
            switch (action.inspectionType) {
              case InspectionScheduleType.DynamicMoveIn:
              case InspectionScheduleType.DynamicMoveOut:
              case InspectionScheduleType.DynamicMakeReady:
              case InspectionScheduleType.DynamicNotice: {
                this.sectionsExpandState['Dynamic'] = this.inspectionsType === InspectionsType.Dynamic;
                break;
              }
              case InspectionScheduleType.Repeating: {
                this.sectionsExpandState['Repeating'] = this.inspectionsType === InspectionsType.Repeating;
                break;
              }
              case InspectionScheduleType.Standard: {
                this.sectionsExpandState['Standard'] = this.inspectionsType === InspectionsType.User;
                this.sectionsExpandState['Resident'] = this.inspectionsType === InspectionsType.User;
                this.sectionsExpandState['Bulk'] = this.inspectionsType === InspectionsType.Bulk;
                break;
              }
            }

            this.cdr.detectChanges();
          }
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.calculateMaxHeight();

    this.setPortfolioId();
    this.checkProperties();

    this.route.url
      .subscribe(segments => {
        const parts = segments.map(s => s.path);
        let inspectionView: InspectionsHeaderViewEnum | null = null;

        if (parts[0] === 'user' || parts[0] === 'resident') {
          if (parts[1] === 'summary') {
            inspectionView = InspectionsHeaderViewEnum.SUMMARY;
          } else {
            inspectionView = InspectionsHeaderViewEnum.LIST;
          }
        }
        if (inspectionView && inspectionView !== this.inspectionsViewType$.value) {
          this.inspectionsViewType$.next(inspectionView);
        }
      })
      .untilDestroyed(this);

    this.updateQueryParams(this.formGroup.value);
  }

  private initQueryTypeChanges(): void {
    this.store
      .select(selectInspectionFilter)
      .pipe(
        map(filter => filter.queryType),
        distinctUntilChanged()
      )
      .subscribe(filter => {
        this.formGroup.patchValue({
          queryType: filter,
        });
      })
      .untilDestroyed(this);

    this.store
      .select(selectRepeatingInspectionFilter)
      .pipe(
        map(filter => filter.queryType),
        distinctUntilChanged()
      )
      .subscribe(filter => {
        this.formGroup.patchValue({
          queryType: filter,
        });
      })
      .untilDestroyed(this);

    this.store
      .select(selectBulkInspectionFilter)
      .pipe(
        map(filter => filter.queryType),
        distinctUntilChanged()
      )
      .subscribe(filter => {
        this.formGroup.patchValue({
          queryType: filter,
        });
      })
      .untilDestroyed(this);

    this.store
      .select(selectInReviewInspectionFilter)
      .pipe(
        map(filter => filter.queryType),
        distinctUntilChanged()
      )
      .subscribe(filter => {
        this.formGroup.patchValue({
          queryType: filter,
        });
      })
      .untilDestroyed(this);
  }

  private initFormGroupListener(): void {
    this.formGroup.valueChanges
      .pipe(debounceTime(600), skipEqual$(), untilDestroyed(this))
      .subscribe(value => {
        let patchForm = false;
        const isCustomPeriod = value.timeframe === this.TimeFrame.Custom;

        if (
          value.timeframe &&
          this.filter?.timeframe &&
          value.timeframe != this.filter?.timeframe &&
          !isCustomPeriod &&
          this.inspectionsType !== InspectionsType.InReview
        ) {
          value.endDate = moment(this._currentDate).toDate();

          switch (value.timeframe) {
            case this.TimeFrame.Week:
              value.startDate = moment(this._currentDate).subtract(7, 'days').toDate();
              break;
            case this.TimeFrame.Month:
              value.startDate = moment(this._currentDate).subtract(30, 'days').toDate();
              break;
            case this.TimeFrame.Month_3:
              value.startDate = moment(this._currentDate).subtract(3, 'months').toDate();
              break;
            case this.TimeFrame.Month_6:
              value.startDate = moment(this._currentDate).subtract(6, 'months').toDate();
              break;
            case this.TimeFrame.Year:
              value.startDate = moment(`${moment(this._currentDate).year() - 1}-01-01T00:00:00.000Z`).toDate();
              value.endDate = moment(`${moment(this._currentDate).year() - 1}-12-31T23:59:59.999Z`).toDate();
              break;
            case this.TimeFrame.ThisWeek:
              value.startDate = moment(this._currentDate).startOf('week').toDate();
              value.endDate = moment(this._currentDate).endOf('week').toDate();
              break;
            case this.TimeFrame.NextTwoWeeks:
              value.startDate = moment(this._currentDate).toDate();
              value.endDate = moment(this._currentDate).add(2, 'weeks').toDate();
              break;
            case this.TimeFrame.ThisMonth:
              value.startDate = moment(this._currentDate).startOf('month').toDate();
              value.endDate = moment(this._currentDate).endOf('month').toDate();
              break;
            case this.TimeFrame.ThisQuarter:
              value.startDate = moment(this._currentDate).startOf('quarter').toDate();
              value.endDate = moment(this._currentDate).endOf('quarter').toDate();
              break;
            case this.TimeFrame.ThisYear:
              value.startDate = moment(this._currentDate).startOf('year').toDate();
              value.endDate = moment(this._currentDate).endOf('year').toDate();
              break;
            case this.TimeFrame.AllTime:
              value.startDate = null;
              value.endDate = null;
              break;
          }

          patchForm = true;
        }

        if (isCustomPeriod) {
          let start = this.startDateControl?.value ? moment(this.startDateControl.value) : moment(this._currentDate);
          if (!start.isValid()) start = moment(this._currentDate);
          this.startDateControl.patchValue(start.toDate(), { emitEvent: false });

          let end = this.endDateControl?.value
            ? moment(this.endDateControl.value)
            : moment(this._currentDate).add(1, 'days');
          if (!end.isValid()) end = moment(this._currentDate).add(1, 'days');
          this.endDateControl.patchValue(end.toDate(), { emitEvent: false });

          value.startDate = start.toDate();
          value.endDate = end.toDate();
        }

        this.updateQueryParams(value);
        this.updateFilters(value);

        if (patchForm) {
          this.formGroup.patchValue(this.filter);
        }
      })
      .untilDestroyed(this);
  }

  private initSummarySettings() {
    this.store
      .select(selectCombinedSummarySetting(SummaryType.Inspection))
      .subscribe(combinedSetting => {
        this.currentSummarySettings = combinedSetting?.modified;
      })
      .untilDestroyed(this);
  }

  private setPortfolioId(): void {
    this.store
      .select(selectSelectedPortfolioId)
      .pipe(
        filterNullish$(),
        tap(portfolioId => {
          this.portfolioId$.next(portfolioId);
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private checkProperties(): void {
    this.store
      .select(selectSelectedPropertyIds)
      .pipe(
        take(1),
        tap(propertyIds => {
          if (propertyIds?.length === 0) {
            this.store.dispatch(setSelectedPropertyIds({ propertyIds: null, allPropertiesSelected: true }));
          }
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  summaryStatsFiltersApplied(value: boolean): void {
    this.summaryModalPending$.next(value);
  }

  private updateFilters(value: Partial<IInspectionFilterForm>): void {
    this.setFilters(value);
    this.updateSummaryFiltersFromList(value);

    if (this.inspectionsType === InspectionsType.InReview) {
      this.store.dispatch(setInReviewInspectionsFilter(value));
    } else if (this.inspectionsType === InspectionsType.Repeating) {
      this.store.dispatch(setRepeatingInspectionsFilter(value));
    } else if (this.inspectionsType === InspectionsType.Bulk) {
      this.store.dispatch(setBulkInspectionFilter(value));
    } else {
      this.store.dispatch(setInspectionsFilter(value));
    }
  }

  private updateSummaryFiltersFromList({ timeframe, startDate, endDate }: Partial<IInspectionFilterForm>): void {
    this.summaryFiltersFromListConfig$.next({
      timeframe,
      startDate,
      endDate,
    });

    const appliedSummaryFilters = getStorageItem<InspectionSummaryAppliedFilters>(
      LocalStorageDataEnum.INSPECTIONS_SUMMARY_FILTERS
    );

    if (appliedSummaryFilters) {
      appliedSummaryFilters.periodId = timeframe;
      appliedSummaryFilters.startDate = startDate?.toISOString() ?? null;
      appliedSummaryFilters.endDate = endDate?.toISOString() ?? null;

      setStorageItem(LocalStorageDataEnum.INSPECTIONS_SUMMARY_FILTERS, appliedSummaryFilters);
    }
  }

  private useLoadUsers() {
    const isResident = this.inspectionsType === InspectionsType.Resident;

    const getTypesResident = [UserType.Resident, UserType.Employee, UserType.Contractor];
    const getTypesBase = [UserType.Employee, UserType.Contractor];
    const userGroupTypes = isResident ? getTypesResident : getTypesBase;

    this.store.dispatch(loadAllUsers({ filter: { userGroupTypes } }));
  }

  private setFilters(value: Partial<IInspectionFilterForm>): void {
    if (this.inspectionsType === InspectionsType.InReview) {
      this.filter = {
        propertyIds: this.filter.propertyIds,
        propertyId: this.filter.propertyId,
        assigneeId: value.assigneeId,
        unitOrAssignee: value.unitOrAssignee,
        templateIds: value.templateIds,
        parentInspectionId: value.parentInspectionId,
        dynamicInspectionId: value.dynamicInspectionId,
        queryType: value.queryType,
      };
    } else if (this.inspectionsType === InspectionsType.Repeating || this.inspectionsType === InspectionsType.Dynamic) {
      this.filter = {
        propertyIds: this.filter.propertyIds,
        propertyId: this.filter.propertyId,
        assigneeId: value.assigneeId,
        unitOrAssignee: value.unitOrAssignee,
        templateIds: value.templateIds,
        recurrencePeriod: value.recurrencePeriod,
        queryType: value.queryType,
      };
    } else if (this.inspectionsType === InspectionsType.Bulk) {
      this.filter = {
        unitOrAssignee: value.unitOrAssignee,
        assigneeId: value.assigneeId,
        templateIds: value.templateIds,
        status: value.status,
        timeframe: value.timeframe,
        startDate: value.startDate,
        endDate: value.endDate,
        queryType: value.queryType,
      };
    } else {
      this.filter = {
        ...this.filter,
        ...value,
      };
    }

    const dataToStore = { ...this.filter } as typeof this.filter & { startDate?: any; endDate?: any };
    if (dataToStore.startDate) dataToStore.startDate = moment(dataToStore.startDate).format('YYYY-MM-DD');
    if (dataToStore.endDate) dataToStore.endDate = moment(dataToStore.endDate).format('YYYY-MM-DD');

    if (!this.doNotStoreParams) setStorageItem(this.filterStorageKey, dataToStore);
  }

  clearFilter(keepTimeFrame = false): void {
    if (this.formGroup.get('unitOrAssignee')) this.formGroup.get('unitOrAssignee').setValue(null);
    if (this.formGroup.get('status')) this.formGroup.get('status').setValue(null);
    if (this.formGroup.get('hasIssues')) this.formGroup.get('hasIssues').setValue(InspectionHasIssues.Any);
    if (this.formGroup.get('assigneeId')) this.formGroup.get('assigneeId').setValue(null);
    if (this.formGroup.get('templateIds')) this.formGroup.get('templateIds').setValue(null);
    if (this.formGroup.get('recurrencePeriod')) this.formGroup.get('recurrencePeriod').setValue(null);
    if (this.formGroup.get('timeframe')) {
      keepTimeFrame
        ? this.formGroup.get('timeframe').setValue(this.formGroup.value.timeframe)
        : this.formGroup.get('timeframe').setValue(InspectionsListConstants.TimeFrame.ThisMonth);
    }
    if (this.formGroup.get('queryType')) this.formGroup.get('queryType').setValue(InspectionQueryType.Active);
    if (this.formGroup.get('parentInspectionId')) this.formGroup.get('parentInspectionId').setValue(null);
    if (this.formGroup.get('dynamicInspectionId')) this.formGroup.get('dynamicInspectionId').setValue(null);
    if (this.formGroup.get('startDate')) {
      keepTimeFrame
        ? this.formGroup.get('startDate').setValue(moment(this._currentDate).subtract(30, 'days').toDate())
        : this.formGroup.get('startDate').setValue(null);
    }
    if (this.formGroup.get('endDate')) {
      keepTimeFrame
        ? this.formGroup.get('endDate').setValue(moment(this._currentDate).add(1, 'days').toDate())
        : this.formGroup.get('endDate').setValue(null);
    }
    if (this.formGroup.get('periodDateSearchFields'))
      this.formGroup.get('periodDateSearchFields').setValue(InspectionsListConstants.TimeWindow.AnyTimeframe);
    if (this.doNotStoreParams) {
      this.doNotStoreParams = false;
      this.filterParams = null;
    }

    if (this.formGroup.get('categoryIds')) {
      this.formGroup.get('categoryIds').setValue(null);
    }

    this.cdr.markForCheck();
  }

  clearSearchControl(): void {
    this.formGroup.patchValue({ unitOrAssignee: null as string });
  }

  addInspection(inspectionFormat = InspectionScheduleType.Standard) {
    this.editInspectionOpened = true;
    if (this.inspectionsType === InspectionsType.Dynamic) {
      inspectionFormat = InspectionScheduleType.DynamicNotice;
    }
    if (this.inspectionsType === InspectionsType.Repeating) {
      inspectionFormat = InspectionScheduleType.Repeating;
    }
    if (this.inspectionsType === InspectionsType.Resident) {
      inspectionFormat = InspectionScheduleType.Resident;
    }

    this.portfolioId$
      .pipe(
        take(1),
        switchMap(portfolioId => {
          if (portfolioId === -1) {
            const config: MatDialogConfig = {
              width: '610px',
              height: 'auto',
              enterAnimationDuration: '0ms',
              panelClass: 'no-padding-dialog',
            };

            const dialogRef = this.dialog.open(SelectPortfolioDialogComponent, config);

            return dialogRef.componentInstance.onSelected.pipe(
              filter(Boolean),
              take(1),
              tap(() => dialogRef.close()),
              switchMap(portfolioId => dialogRef.afterClosed().pipe(map(() => portfolioId)))
            );
          }

          return of(portfolioId);
        }),
        switchMap(portfolioId => {
          return this.dialog
            .open<InspectionAddDialogComponent, InspectionDialogData>(InspectionAddDialogComponent, {
              data: {
                inspectionFormat,
                portfolioId,
              },
            })
            .afterClosed()
            .pipe(take(1));
        })
      )
      .subscribe(() => {
        this.editInspectionOpened = false;
      })
      .untilDestroyed(this);
  }

  toggleSection(name: string, isExpanded: boolean) {
    if (isExpanded) {
      window.dispatchEvent(new Event('resize'));
    }
  }

  addNonPmsUnit() {
    const config: MatDialogConfig = {
      ...DefaultModalConfigConstant,
      width: '400px',
      panelClass: 'user-preview-modal-container',
    };

    this.dialog
      .open<void, void, { unitName: string; propertyId: number; unitIconType: string }>(NonPmsAddModalComponent, config)
      .afterClosed()
      .pipe(
        take(1),
        filter(unitInfo => !!unitInfo),
        map(({ unitName, propertyId, unitIconType }) => ({
          propertyId: propertyId,
          unitName: unitName,
          unitIconType,
        })),
        switchMap(({ propertyId, unitName, unitIconType }) => {
          if (!unitName) {
            return of({});
          }
          this.isUnitAdding$.next(true);
          return this.unitNonPmsService
            .add({
              propertyId: propertyId,
              name: unitName,
              unitIconType,
            })
            .pipe(
              tap((unitId: number) => {
                this.snackbarService.success(`Unit ${unitName} created`);
              })
            );
        }),
        finalize(() => {
          this.isUnitAdding$.next(false);
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initTemplates(): Observable<IRadioButtonOption<number>[]> {
    return this.store.select(selectTemplates).pipe(
      filterNullish$(),
      map(list =>
        list.filter((e, i, array) => array.indexOf(e) === i && e).map((e, i) => ({ value: e.id, label: e.name }))
      )
    );
  }

  recurrenceDividerPosition: number = null;

  private initRecurenceOptions(): Observable<IRadioButtonOption<InspectionRecurrencePeriod | string>[]> {
    return combineLatest([
      this.store.select(selectRepeatingInspectionsLoading),
      this.store.select(selectRepeatingInspections),
      this.store.select(selectSelectedPropertyIds),
    ]).pipe(
      filter(([loading, inspections]) => !loading && inspections != null),
      map(([_, inspections, propertyIds]) => {
        const filtered = inspections?.filter(i => propertyIds.includes(i.propertyId ?? i.unit?.propertyId));

        const seenLabels = new Set<string>();
        const seenGeneral = new Set<number>();
        const specificOptions: IRadioButtonOption<number | string>[] = [];

        for (const inspection of filtered ?? []) {
          if (inspection.recurrencePeriod == null || !inspection.dueDate) continue;

          const options = GetRecurrencePeriodTypeList(new Date(inspection.dueDate));
          const match = options.find(opt => opt.value === inspection.recurrencePeriod);

          if (match) {
            if (!seenLabels.has(match.label)) {
              seenLabels.add(match.label);
              specificOptions.push({
                value: match.label,
                label: match.label,
              });
            }

            if (!seenGeneral.has(match.value)) {
              seenGeneral.add(match.value);
            }
          }
        }

        const generalize = (value: InspectionRecurrencePeriod): InspectionRecurrencePeriod => {
          switch (value) {
            case InspectionRecurrencePeriod.SameWeekMonthly:
              return InspectionRecurrencePeriod.Monthly;
            case InspectionRecurrencePeriod.SameWeekQuarterly:
              return InspectionRecurrencePeriod.Quarterly;
            default:
              return value;
          }
        };

        const getRecurrenceLabel = (value: InspectionRecurrencePeriod): string => {
          const raw = InspectionRecurrencePeriod[value];
          if (!raw) return 'Unknown';

          if (value === InspectionRecurrencePeriod.Daily) return 'Weekdays';
          if (value === InspectionRecurrencePeriod.DoNotRepeat) return 'Does not repeat';

          return raw.replace(/([a-z])([A-Z])/g, (_, a, b) => `${a}-${b.toLowerCase()}`);
        };

        const generalizedValues = Array.from(seenGeneral)
          .map(generalize)
          .filter((value, index, self) => self.indexOf(value) === index);

        const generalOptions: IRadioButtonOption<number | string>[] = generalizedValues
          .map(value => ({
            value,
            label: getRecurrenceLabel(value),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        const sortedSpecificOptions = specificOptions
          .filter(option => option.label !== 'Weekdays')
          .sort((a, b) => a.label.localeCompare(b.label));

        this.recurrenceDividerPosition = generalOptions?.length > 1 ? generalOptions?.length - 1 : null;

        return [...generalOptions, ...sortedSpecificOptions];
      })
    );
  }

  private initTemplatesWithParentInspectionId(): Observable<IRadioButtonOption<number>[]> {
    return combineLatest([
      this.store.select(selectInspections),
      this.store.select(selectInReviewInspections),
      this.store.select(selectTemplates),
    ]).pipe(
      filter(([templates]) => !!templates),
      map(([inspectionList, inReviewInspectionList, templates]) => {
        const inspections = this.inspectionsType === InspectionsType.InReview ? inReviewInspectionList : inspectionList;
        const uniqueParentIds = new Set<number>();
        return inspections
          .filter(inspection => inspection.parentInspectionId)
          .filter(inspection => {
            if (!uniqueParentIds.has(inspection.parentInspectionId)) {
              uniqueParentIds.add(inspection.parentInspectionId);
              return true;
            }
            return false;
          })
          .map(inspection => {
            const template = templates.find(t => t.id === inspection.inspectionTemplateId);
            const templateName = template ? template.name : 'Unknown Template';
            return {
              value: inspection.parentInspectionId,
              label: `${inspection.unit.property.name} - ${templateName}`,
            };
          });
      })
    );
  }

  private initTemplatesWithDynamicInspectionId(): Observable<IRadioButtonOption<number>[]> {
    return combineLatest([
      this.store.select(selectInspections),
      this.store.select(selectInReviewInspections),
      this.store.select(selectTemplates),
    ]).pipe(
      filter(
        ([inspectionList, inReviewInspectionList, templates]) =>
          !!inspectionList && !!inReviewInspectionList && !!templates
      ),
      map(([inspectionList, inReviewInspectionList, templates]) => {
        const inspections = this.inspectionsType === InspectionsType.InReview ? inReviewInspectionList : inspectionList;
        const uniqueDynamicIds = new Set<number>();
        return inspections
          .filter(inspection => inspection.dynamicInspectionId)
          .filter(inspection => {
            if (!uniqueDynamicIds.has(inspection.dynamicInspectionId)) {
              uniqueDynamicIds.add(inspection.dynamicInspectionId);
              return true;
            }
            return false;
          })
          .map(inspection => {
            const template = templates.find(t => t.id === inspection.inspectionTemplateId);
            const templateName = template ? template.name : 'Unknown Template';
            return {
              value: inspection.dynamicInspectionId,
              label: ` ${inspection.unit.property.name} - ${templateName}`,
            };
          });
      })
    );
  }

  private initFormGroup(): FormGroup<IForm<Partial<IInspectionFilterForm>>> {
    if (this.inspectionsType === InspectionsType.InReview) {
      return this.formBuilder.group({
        unitOrAssignee: null,
        assigneeId: null,
        templateIds: null,
        parentInspectionId: null,
        dynamicInspectionId: null,
        queryType: InspectionQueryType.Active,
      }) as FormGroup<IForm<Partial<IInspectionFilterForm>>>;
    }

    if (this.inspectionsType === InspectionsType.Repeating) {
      return this.formBuilder.group({
        unitOrAssignee: null,
        assigneeId: null,
        templateIds: null,
        recurrencePeriod: null,
        queryType: InspectionQueryType.Active,
      }) as FormGroup<IForm<Partial<RepeatingInspectionFilterForm>>>;
    }

    if (this.inspectionsType === InspectionsType.Bulk) {
      return this.formBuilder.group({
        unitOrAssignee: null,
        assigneeId: null,
        templateIds: null,
        status: null,
        queryType: InspectionQueryType.Active,
        startDate: null,
        endDate: null,
        timeframe: InspectionsListConstants.TimeFrame.ThisMonth,
        periodDateSearchFields: InspectionsListConstants.TimeWindow.AnyTimeframe,
      }) as FormGroup<IForm<Partial<BulkInspectionFilterForm>>>;
    }

    return this.formBuilder.group({
      unitOrAssignee: null,
      status: null,
      inspectionByStatusFilter: null,
      // isBulk: null,
      assigneeId: null,
      templateIds: null,
      recurrencePeriod: null,
      timeframe: InspectionsListConstants.TimeFrame.ThisMonth,
      queryType: InspectionQueryType.Active,
      parentInspectionId: null,
      dynamicInspectionId: null,
      startDate: null,
      endDate: null,
      periodDateSearchFields: InspectionsListConstants.TimeWindow.AnyTimeframe,
      categoryIds: null,
      hasIssues: InspectionHasIssues.Any,
    }) as FormGroup<IForm<Partial<IInspectionFilterForm>>>;
  }

  getDynamicInspectionsIds(): Observable<{ dynamicInspectionId: number; count: number }[]> {
    const inspections$ =
      this.inspectionsType === InspectionsType.InReview
        ? this.store.select(selectInReviewInspections)
        : this.store.select(selectInspections);

    return inspections$.pipe(
      filterNullish$(),
      map(inspections => {
        const inspectionCountMap = new Map<number, number>();

        inspections
          .filter(inspection => inspection.dynamicInspectionId)
          .forEach(inspection => {
            const dynamicId = inspection.dynamicInspectionId;
            if (inspectionCountMap.has(dynamicId)) {
              inspectionCountMap.set(dynamicId, inspectionCountMap.get(dynamicId)! + 1);
            } else {
              inspectionCountMap.set(dynamicId, 1);
            }
          });

        return Array.from(inspectionCountMap, ([dynamicInspectionId, count]) => ({
          dynamicInspectionId,
          count,
        }));
      })
    );
  }

  getRepeatingInspectionsIds(): Observable<{ parentInspectionId: number; count: number }[]> {
    const inspections$ =
      this.inspectionsType === InspectionsType.InReview
        ? this.store.select(selectInReviewInspections)
        : this.store.select(selectInspections);

    return inspections$.pipe(
      filterNullish$(),
      map(inspections => {
        const inspectionCountMap = new Map<number, number>();

        inspections
          .filter(inspection => inspection.parentInspectionId)
          .forEach(inspection => {
            const parentId = inspection.parentInspectionId;
            if (inspectionCountMap.has(parentId)) {
              inspectionCountMap.set(parentId, inspectionCountMap.get(parentId)! + 1);
            } else {
              inspectionCountMap.set(parentId, 1);
            }
          });

        return Array.from(inspectionCountMap, ([parentInspectionId, count]) => ({
          parentInspectionId,
          count,
        }));
      })
    );
  }

  downloadAllReports() {
    this.bulkDownloadService.runDownloadingProcess();
  }

  calculateMaxHeight(filterPanelEmpty = true): void {
    setTimeout(() => {
      const itemHeight = 40;
      const screenHeight = window.innerHeight;
      const topContentHeight = filterPanelEmpty ? 210 : 240;
      this.maxHeight = Math.floor(screenHeight / itemHeight) * itemHeight - topContentHeight;
    });
  }

  public changedInspectionsViewType(view: InspectionsHeaderViewEnum, doNotStoreParams = false): void {
    this.clearSearchControl();
    if (!doNotStoreParams) this.inspectionService.updateInspectionsViewModeInStorage(view);

    this.updateRouteAndState(view);
  }

  private updateRouteAndState(view: InspectionsHeaderViewEnum): void {
    if (this.inspectionsViewType$.value === InspectionsHeaderViewEnum.SUMMARY && !this.canInspectionsRedirect()) {
      this.inspectionHeader?.inspectionViewToggle?.viewModeControl.setValue(InspectionsHeaderViewEnum.SUMMARY);
      this.cdr.detectChanges();
      return;
    }

    this.inspectionsViewType$.next(view);

    const urlSegments = this.route.snapshot.url.map(seg => seg.path);
    const rootSegment = urlSegments[0] ?? 'user';

    let path: any[];

    if (view === InspectionsHeaderViewEnum.SUMMARY) {
      path = ['/inspections', rootSegment, 'summary'];
    } else {
      path = ['/inspections', rootSegment];
    }

    const { inspectionsView, ...queryParams } = this.route.snapshot.queryParams;

    this.router
      .navigate(path, {
        queryParams,
        queryParamsHandling: 'merge',
      })
      .then();
  }

  private getExcludedFromFilterCounter(): string[] {
    if (this.inspectionsType === InspectionsType.InReview) {
      return [
        'doNotReturnContent',
        'inReviewOnly',
        'isResident',
        'returnStandardAndResidentBoth',
        'unitOrAssignee',
        'queryType',
      ];
    }
    if (this.inspectionsType === InspectionsType.Bulk) {
      return ['unitOrAssignee', 'startDate', 'endDate', 'queryType', 'propertyIds', 'timeframe'];
    }
    return ['unitOrAssignee', 'startDate', 'endDate', 'queryType'];
  }

  onOpenFilter(item: string): void {
    this.openFilter = { item };
  }

  onOpenDropdown(item: string): void {
    setTimeout(() => {
      this.openDropdown = { item };
      this.cdr.detectChanges();
    }, 200);
    setTimeout(() => {
      this.openDropdown = {};
    }, 300);
  }

  onFilterPaneChange(value: Partial<IInspectionFilterForm>): void {
    this.formGroup.patchValue({ ...this.formGroup.value, ...value });

    this.inspectionByStatusFilter = this.getInspectionByStatusFilterLabel(
      this.formGroup.value.inspectionByStatusFilter
    );

    this.updateQueryParams(this.formGroup.value);
  }

  protected readonly Object = Object;

  private onApplyFilters(): void {
    const filtersFromStorage = getStorageItem<IInspectionFilterForm>(this.filterStorageKey);

    if (filtersFromStorage?.hasOwnProperty('inspectionByStatusFilter')) {
      delete filtersFromStorage.inspectionByStatusFilter;
    }

    let savedFilterState: Partial<IInspectionFilterForm>;
    if (this.doNotStoreParams || this.showInstances) {
      savedFilterState = this.filterParams;
    } else {
      savedFilterState = { ...this.filterParams, ...filtersFromStorage };
    }

    const startDateValue = savedFilterState?.startDate
      ? moment(savedFilterState.startDate).toDate()
      : savedFilterState.timeframe !== this.TimeFrame.AllTime
      ? moment(this._currentDate).toDate()
      : null;

    const endDateValue = savedFilterState?.endDate
      ? moment(savedFilterState.endDate).toDate()
      : savedFilterState.timeframe !== this.TimeFrame.AllTime
      ? moment(this._currentDate).add(1, 'days').toDate()
      : null;

    savedFilterState.startDate = startDateValue;
    savedFilterState.endDate = endDateValue;

    if (savedFilterState.hasOwnProperty('portfolioId')) delete savedFilterState['portfolioId'];

    if (savedFilterState && this.inspectionsType === InspectionsType.InReview) {
      this.store.dispatch(setInReviewInspectionsFilter(savedFilterState));
      this.formGroup.patchValue(savedFilterState);
      this.setFilters(savedFilterState);
      return;
    }

    if (savedFilterState && this.inspectionsType === InspectionsType.Repeating) {
      this.store.dispatch(setRepeatingInspectionsFilter(savedFilterState));
      this.formGroup.patchValue(savedFilterState);
      this.setFilters(savedFilterState);
      return;
    }

    if (savedFilterState && this.inspectionsType === InspectionsType.Bulk) {
      this.store.dispatch(setBulkInspectionFilter(savedFilterState));
      this.formGroup.patchValue(savedFilterState);
      this.setFilters(savedFilterState);
      return;
    }

    if (savedFilterState) {
      this.store.dispatch(setInspectionsFilter(savedFilterState));
      this.formGroup.patchValue(savedFilterState);
      this.setFilters(savedFilterState);
    }
  }

  private onApplyQueryParams(): void {
    this.route.queryParams
      .pipe(take(1))
      .subscribe(params => {
        const paramsWithInspectionByStatusFilter = {
          ...params,
          inspectionByStatusFilter: this.route.snapshot.paramMap.get('inspectionByStatusFilter'),
        };

        const parentInspectionInstance = +params['parentInspectionInstance'];
        const dynamicInspectionInstance = +params['dynamicInspectionInstance'];
        if (
          (parentInspectionInstance && !isNaN(parentInspectionInstance)) ||
          (dynamicInspectionInstance && !isNaN(dynamicInspectionInstance))
        ) {
          this.filterParams = {
            parentInspectionId: parentInspectionInstance ? [parentInspectionInstance] : null,
            dynamicInspectionId: dynamicInspectionInstance ? [dynamicInspectionInstance] : null,
            timeframe: InspectionsListConstants.TimeFrame.AllTime,
            status: this.statuses?.filter(el => el.value !== InspectionStatus.Completed)?.map(el => el.value),
            queryType: null,
          };

          this.showInstances = true;
          return;
        }
        this.showInstances = false;

        const extractedParams = extractFiltersFromParams(
          paramsWithInspectionByStatusFilter,
          [
            'assigneeId',
            'templateIds',
            'dynamicInspectionId',
            'parentInspectionId',
            'status',
            'recurrencePeriod',
            'periodDateSearchFields',
            'categoryIds',
          ],
          ['timeframe', 'queryType', 'inspectionByStatusFilter'],
          ['startDate', 'endDate']
        );

        this.filterParams = normalizeQueryParams(extractedParams, ['portfolioId']);
        this.inspectionByStatusFilter = this.getInspectionByStatusFilterLabel(
          this.filterParams.inspectionByStatusFilter
        );
      })
      .untilDestroyed(this);
  }

  private updateQueryParams(value: { [key: string]: any }): void {
    if (this.doNotStoreParams && this.isReportView) return;
    if (this.inspectionsViewType$.getValue() !== InspectionsHeaderViewEnum.LIST) return;
    const currentParams = { ...this.route.snapshot.queryParams };
    const nextParams = { ...currentParams };

    const itemsFilterOut = ['portfolioId', 'propertyIds', 'doNotStoreParams'];
    if (value.timeframe !== this.TimeFrame.Custom) itemsFilterOut.push('startDate', 'endDate');

    const filteredFromValues = normalizeQueryParams(value, itemsFilterOut);

    const normalized = convertFiltersToParams(
      filteredFromValues,
      [
        'assigneeId',
        'templateIds',
        'dynamicInspectionId',
        'parentInspectionId',
        'status',
        'inspectionByStatusFilter',
        'recurrencePeriod',
        'periodDateSearchFields',
        'queryType',
        'categoryIds',
        'hasIssues',
      ],
      true
    );

    const controlledKeys: (keyof typeof normalized | string)[] = [
      'timeframe',
      'startDate',
      'endDate',
      'assigneeId',
      'templateIds',
      'dynamicInspectionId',
      'parentInspectionId',
      'status',
      'inspectionByStatusFilter',
      'recurrencePeriod',
      'periodDateSearchFields',
      'queryType',
      'categoryIds',
      'hasIssues',
    ];

    for (const k of controlledKeys) {
      if (!(k in normalized)) delete nextParams[k];
    }

    for (const [k, v] of Object.entries(normalized)) {
      nextParams[k] = v;
    }

    nextParams['portfolioId'] = this.portfolioId$.getValue();

    for (const key of Object.keys(nextParams)) {
      if (/Id(s?)$/.test(key)) {
        if (!(key in normalized)) {
          const nameKey = key.replace(/Id(s?)$/, (_, plural) => (plural ? 'Names' : 'Name'));

          delete nextParams[key];
          if (nameKey in nextParams) {
            delete nextParams[nameKey];
          }
        }
      }
    }

    if (this.doNotStoreParams || currentParams.doNotStoreParams === 'true' || currentParams.doNotStoreParams === true) {
      nextParams['doNotStoreParams'] = true;
    }

    const hasDates = !!nextParams['startDate'] && !!nextParams['endDate'];
    if (hasDates) delete nextParams['timeframe'];
    else if (value.timeframe !== this.TimeFrame.Custom) {
      delete nextParams['startDate'];
      delete nextParams['endDate'];
    }

    this.router.navigate([], { relativeTo: this.route, queryParams: nextParams, replaceUrl: true });
  }

  private getInspectionByStatusFilterLabel(filter: InspectionByStatusFilter): string {
    return InspectionByStatusFilterLabels[filter];
  }

  private isCurrentYear(date: Date | string | moment.Moment): boolean {
    const inputYear = moment(date).year();
    const currentYear = moment(this._currentDate).year();
    return inputYear === currentYear;
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (!this.inspectionHeader || !this.inspectionHeader.isDirty || this.canBeDestroyed) return true;
    this.canBeDestroyed = this.canInspectionsRedirect();
    return this.canBeDestroyed;
  }

  canInspectionsRedirect(): boolean {
    if (this.inspectionsViewType$.value !== InspectionsHeaderViewEnum.SUMMARY) return true;
    const message = 'You have unsaved changes. Are you sure you want to leave the page?';
    if (
      this.inspectionHeader.inspectionsSummaryLabel?.value === -1 &&
      (this.currentSummarySettings?.filterBy?.length || this.currentSummarySettings?.showBy?.length)
    ) {
      return confirm(message);
    } else if (this.inspectionHeader.inspectionsSummaryLabel?.value !== -1 && this.inspectionHeader?.isDirty) {
      return confirm(message);
    }
    return true;
  }

  onArchiveAll(): void {
    this.archiveAll$.next(new Date().getTime());
  }
}
